// ---------------------------------------------------------------
// Product definitions — one codebase, several apps.
//
// TagAlong (consumer)  : one family, a few cars, "where is my car and is it OK".
// TagAlong Fleet (B2B) : a company, many vehicles and drivers, "who is driving
//                        what, is the fleet running well, and prove it on paper".
//
// Everything that differs between the apps is declared HERE rather than being
// sprinkled through the UI as `IS_TAGALONG ? ... : ...` checks. When a third
// product appears, it should be a new entry in this file and nothing else.
//
// Selected at build time:  REACT_APP_PRODUCT=fleet npm run build
// or at runtime by domain/query for previewing (?fleet).
// ---------------------------------------------------------------

const COMMON = {
  supportPhone: '3055040711',
  supportPhoneDisplay: '(305) 504-0711',
  company: 'Dynamic BPO LLC',
};

export const PRODUCTS = {
  tagalong: {
    ...COMMON,
    key: 'tagalong',
    name: 'TagAlong',
    tagline: 'Know where your car is — always.',
    bundleId: 'com.dynamicsbpo.tagalong',
    // who the app is for, in the app's own words
    audience: 'consumer',
    features: {
      drivers: false,        // assign drivers to vehicles, per-driver scoring
      fleetDashboard: false, // multi-vehicle overview table
      reports: false,        // scheduled reports + CSV/PDF export
      roles: false,          // multiple logins per account, permission levels
      dispatch: false,       // job assignment / AI dispatching
      routeOptimization: false,
      trackAndTrace: false,  // customer-facing shipment tracking links
      shop: true,            // consumer hardware store
      community: true,       // broker community feed
      rentals: true,         // rental agreements
    },
  },

  // TagAlong Pro — the consumer app, re-skinned for a company. Same screens
  // and same tracking core as consumer TagAlong; the only functional change is
  // rentals are off (a company tracking its own vehicles isn't renting them
  // out). Heavier company features live in Fleet, not here.
  pro: {
    ...COMMON,
    key: 'pro',
    name: 'TagAlong Pro',
    tagline: 'Your company vehicles, always in view.',
    bundleId: 'com.dynamicsbpo.tagalongpro',
    audience: 'business',
    features: {
      drivers: false,
      fleetDashboard: false,
      reports: false,
      roles: false,
      dispatch: false,
      routeOptimization: false,
      trackAndTrace: false,
      shop: true,
      community: true,
      rentals: false, // explicitly off for Pro
    },
  },

  fleet: {
    ...COMMON,
    key: 'fleet',
    name: 'TagAlong Fleet',
    tagline: 'Every vehicle, every driver, one screen.',
    bundleId: 'com.dynamicsbpo.tagalongfleet',
    audience: 'business',
    features: {
      drivers: true,
      fleetDashboard: true,
      reports: true,
      roles: true,
      dispatch: true,
      routeOptimization: true,
      trackAndTrace: true,
      shop: false,       // fleet buys hardware on contract, not in-app
      community: false,  // consumer/broker social features don't belong here
      rentals: false,
    },
  },
};

// ---- which product is this build? ----
function detect() {
  // 1) An explicit portal in the URL (/fleet, /pro, ?fleet, ?pro, ?tagalong)
  //    ALWAYS wins. This is deliberate user intent and must override the build's
  //    REACT_APP_PRODUCT env var — otherwise a build pinned to `tagalong` can
  //    never show the /pro or /fleet portal no matter the URL.
  if (typeof window !== 'undefined') {
    const qs = window.location.search || '';
    const path = window.location.pathname || '';
    if (/^\/fleet/i.test(path) || /[?&]fleet\b/i.test(qs)) return 'fleet';
    if (/^\/pro/i.test(path) || /[?&]pro\b/i.test(qs)) return 'pro';
    if (/[?&]tagalong\b/i.test(qs)) return 'tagalong';
  }
  // 2) Otherwise honor the build-time product, if set.
  const envKey = process.env.REACT_APP_PRODUCT;
  if (envKey && PRODUCTS[envKey]) return envKey;
  // 3) Fall back to the domain (pro.* / *fleet* / *tagalong*).
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (/fleet/i.test(host)) return 'fleet';
    if (/(^|\.)pro\./i.test(host)) return 'pro';
    if (/tagalong/i.test(host)) return 'tagalong';
  }
  return 'tagalong';
}

export const PRODUCT = PRODUCTS[detect()];
export const IS_FLEET = PRODUCT.key === 'fleet';
export const IS_PRO = PRODUCT.key === 'pro';

// The TagAlong customer nav items an admin can show/hide per profile. Paths must
// match TAGALONG_MENU in App.js. Used by the admin Customers page (to toggle)
// and by App.js (to hide what a given account's appConfig turns off).
export const TAGALONG_ACCESS = [
  { path: '/map', name: 'Live Tracking' },
  { path: '/car', name: 'TagAlong' },
  { path: '/history', name: 'Trip History' },
  { path: '/vehicles', name: 'Vehicles' },
  { path: '/rentals', name: 'Rentals' },
  { path: '/analytics', name: 'Analytics' },
  { path: '/health', name: 'Vehicle Health' },
  { path: '/alerts', name: 'Alerts' },
  { path: '/shop', name: 'Shop' },
];

// Feature check used throughout the UI:  if (has('drivers')) { ... }
export const has = (feature) => !!PRODUCT.features[feature];
